import React, { useMemo, useState } from 'react';
import { money } from './money';

const NAVY = '#17365f';
const H = 320;
const TOP = 48;
const BOTTOM = 20;
const DEFAULT_LABEL_WIDTH = 180;
const DEFAULT_WEEK_WIDTH = 92;
const DEFAULT_TABLE_WIDTH = 1496;

const METRICS = {
  ending: {
    key: 'endingCash',
    tab: 'Ending cash',
    title: 'Ending cash by week',
  },
  cashIn: { key: 'totalCashIn', tab: 'Cash in', title: 'Cash in by week' },
  cashOut: { key: 'totalCashOut', tab: 'Cash out', title: 'Cash out by week' },
  net: { key: 'netCashFlow', tab: 'Net cash', title: 'Net cash flow by week' },
};

function compact(value) {
  const amount = Math.abs(Number(value || 0));
  const body =
    amount >= 1000000
      ? `${(amount / 1000000).toFixed(1)}M`
      : `${Math.round(amount / 1000)}k`;
  return `${Number(value) < 0 ? '−' : ''}$${body}`;
}

function riskOf(value, floor, caution) {
  if (value < floor) return 'floor';
  if (value < caution) return 'caution';
  return 'safe';
}

const riskText = {
  floor: 'text-red-700',
  caution: 'text-amber-700',
  safe: 'text-gray-900',
};

export default function LiquidityChart({
  weekly = [],
  floor = 0,
  caution = 0,
  onSelect,
  onHoverWeek,
  hoveredWeek = null,
  selectedWeek = 0,
  tableWidth = DEFAULT_TABLE_WIDTH,
  labelWidth = DEFAULT_LABEL_WIDTH,
  weekWidth = DEFAULT_WEEK_WIDTH,
}) {
  const [metric, setMetric] = useState('ending');
  const [hovered, setHovered] = useState(null);
  const activeHover = hoveredWeek ?? hovered;
  const config = METRICS[metric];
  const active = Math.min(
    weekly.length - 1,
    Math.max(0, activeHover ?? selectedWeek ?? 0),
  );
  const width = tableWidth;
  const values = useMemo(
    () =>
      weekly.map((week) => {
        const value = Number(week[config.key] || 0);
        return metric === 'cashOut' ? -value : value;
      }),
    [config.key, metric, weekly],
  );
  const geometry = useMemo(() => {
    const minimum = Math.min(0, ...values);
    const maximum = Math.max(
      ...values,
      config.key === 'endingCash' ? Math.max(1000000, caution) : 1,
    );
    const span = Math.max(1, maximum - minimum);
    const padding = span * 0.12;
    const min = minimum - padding * (minimum < 0 ? 1 : 0.15);
    const max = maximum + padding;
    const plotBottom = H - BOTTOM;
    const y = (value) =>
      TOP + ((max - value) / (max - min)) * (plotBottom - TOP);
    const x = (index) => labelWidth + (index + 0.5) * weekWidth;
    return {
      minimum,
      maximum,
      x,
      y,
      zero: y(0),
      barW: Math.min(22, weekWidth * 0.2),
    };
  }, [caution, config.key, labelWidth, values, weekWidth]);
  const week = weekly[active];
  const value = values[active] ?? 0;
  const ticks = [geometry.minimum, 0, geometry.maximum].filter(
    (tick, index, list) => list.indexOf(tick) === index,
  );
  const linePath = values
    .map(
      (item, index) =>
        `${index ? 'L' : 'M'} ${geometry.x(index)} ${geometry.y(item)}`,
    )
    .join(' ');
  const selectedTone =
    metric === 'ending'
      ? riskText[riskOf(Number(week?.endingCash || 0), floor, caution)]
      : (metric === 'net' || metric === 'cashOut') && value < 0
        ? 'text-red-700'
        : 'text-gray-900';

  if (!weekly.length) return null;

  return (
    <div
      className="border-y border-gray-200 bg-white"
      style={{ width: `${width}px` }}
    >
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
        <div className="flex flex-wrap gap-1.5" role="tablist">
          {Object.entries(METRICS).map(([id, item]) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={id === metric}
              onClick={() => setMetric(id)}
              className={`inline-flex h-[31px] items-center rounded border px-3 text-[10px] font-bold uppercase tracking-[.12em] ${
                id === metric
                  ? 'border-[#17365f] bg-[#17365f] text-white'
                  : 'border-gray-300 bg-white text-gray-500'
              }`}
            >
              {item.tab}
            </button>
          ))}
        </div>
        <div className="text-right">
          <p className={`text-[17px] font-bold tabular-nums ${selectedTone}`}>
            {money(metric === 'cashOut' ? value : value)}
          </p>
          <p className="text-[11px] font-bold uppercase tracking-[.13em] text-gray-900">
            {config.title} · Wk {active + 1}
          </p>
        </div>
      </div>
      <div>
        <svg
          width={width}
          height={H}
          className="block"
          role="img"
          aria-label={config.title}
          onMouseLeave={() => {
            setHovered(null);
            onHoverWeek?.(null);
          }}
        >
          <defs>
            <linearGradient id="twcf-bar-green" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#15803d" stopOpacity=".8" />
              <stop offset="1" stopColor="#86efac" stopOpacity=".2" />
            </linearGradient>
            <linearGradient id="twcf-bar-red" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#b91c1c" stopOpacity=".8" />
              <stop offset="1" stopColor="#fca5a5" stopOpacity=".2" />
            </linearGradient>
            <linearGradient id="twcf-bar-navy" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor={NAVY} stopOpacity=".72" />
              <stop offset="1" stopColor="#aebbd0" stopOpacity=".2" />
            </linearGradient>
          </defs>
          {ticks.map((tick) => (
            <g key={tick}>
              <line
                x1={labelWidth}
                x2={labelWidth + weekWidth * weekly.length}
                y1={geometry.y(tick)}
                y2={geometry.y(tick)}
                stroke="#e8ebef"
              />
              <text
                x={labelWidth - 9}
                y={geometry.y(tick) + 3}
                textAnchor="end"
                fill="#6b7280"
                fontSize="11"
              >
                {compact(tick)}
              </text>
            </g>
          ))}
          {metric === 'ending' && (
            <>
              <line
                x1={labelWidth}
                x2={labelWidth + weekWidth * weekly.length}
                y1={geometry.y(caution)}
                y2={geometry.y(caution)}
                stroke="#b45309"
                strokeDasharray="3 4"
              />
              <line
                x1={labelWidth}
                x2={labelWidth + weekWidth * weekly.length}
                y1={geometry.y(floor)}
                y2={geometry.y(floor)}
                stroke="#b91c1c"
                strokeDasharray="3 4"
              />
            </>
          )}
          <line
            x1={labelWidth}
            x2={labelWidth + weekWidth * weekly.length}
            y1={geometry.zero}
            y2={geometry.zero}
            stroke="#cbd5e1"
          />
          {weekly.map((item, index) => {
            const plotted = values[index];
            const selected = index === active;
            const positive =
              metric === 'net' ? plotted >= 0 : metric !== 'cashOut';
            const fill =
              metric === 'ending'
                ? 'url(#twcf-bar-navy)'
                : positive
                  ? 'url(#twcf-bar-green)'
                  : 'url(#twcf-bar-red)';
            return (
              <g key={`bar-${item.start || index}`}>
                {selected && (
                  <rect
                    x={labelWidth + index * weekWidth}
                    y={TOP}
                    width={weekWidth}
                    height={H - TOP}
                    fill="#f3f4f6"
                    fillOpacity={1}
                  />
                )}
                {activeHover === index && (
                  <rect
                    x={labelWidth + index * weekWidth}
                    y={TOP}
                    width={weekWidth}
                    height={H - TOP}
                    fill="#9ca3af"
                    fillOpacity={0.18}
                  />
                )}
                <rect
                  x={geometry.x(index) - geometry.barW / 2}
                  y={Math.min(geometry.y(plotted), geometry.zero)}
                  width={geometry.barW}
                  height={Math.max(
                    1,
                    Math.abs(geometry.y(plotted) - geometry.zero),
                  )}
                  fill={fill}
                  stroke={selected ? NAVY : '#c8d0da'}
                />
                <rect
                  x={labelWidth + index * weekWidth}
                  y={TOP}
                  width={weekWidth}
                  height={H - TOP}
                  fill="transparent"
                  className="cursor-crosshair"
                  onMouseEnter={() => {
                    setHovered(index);
                    onHoverWeek?.(index);
                  }}
                  onClick={() => onSelect?.(index)}
                />
              </g>
            );
          })}
          <path
            d={linePath}
            fill="none"
            stroke="#8d99aa"
            strokeWidth="1.25"
            strokeDasharray="4 4"
          />
          {week && (
            <>
              <line
                x1={geometry.x(active)}
                x2={geometry.x(active)}
                y1={TOP}
                y2={geometry.zero}
                stroke={NAVY}
              />
              <circle
                cx={geometry.x(active)}
                cy={geometry.y(value)}
                r="3.5"
                fill={NAVY}
              />
            </>
          )}
        </svg>
      </div>
    </div>
  );
}
